import { formatBedroomOptions, formatBedroomLabel} from "@/lib/utils";
import { useState, useEffect } from "react";
import { LazySection } from "@/components/shared/LazySection";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  MapPin, Bed, Bath, Square, Phone, ArrowLeft, Calendar, Home,
  CheckCircle, Mail, Edit, Share2, Heart, Building2, Car, Layers,
  Ruler, Sofa, Shield
} from "lucide-react";
import { WhatsAppShare } from "@/components/shared/WhatsAppShare";
import { EmailShare } from "@/components/shared/EmailShare";
import { PropertyShare } from "@/components/shared/PropertyShare";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { InquiryForm } from "@/components/public/InquiryForm";
import { Navbar } from "@/components/public/Navbar";
import EnhancedPropertyImageGallery from "@/components/shared/EnhancedPropertyImageGallery";
import { AppointmentBooking } from "@/components/public/AppointmentBooking";
import { AuthGatedDialog } from "@/components/shared/AuthGatedDialog";
import PropertyMap from "@/components/maps/PropertyMap";
import { PropertyDocuments } from "@/components/public/PropertyDocuments";
import { YouTubeEmbed } from "@/components/shared/YouTubeEmbed";
import { formatPriceDisplay } from "@/utils/currency";
import SimilarProperties from "@/components/public/SimilarProperties";
import { MortgageCalculator } from "@/components/public/MortgageCalculator";
import { NeighborhoodInsights } from "@/components/public/NeighborhoodInsights";
import { PropertyRating } from "@/components/public/PropertyRating";
import { PropertyRatingCompact } from "@/components/public/PropertyRatingCompact";
import { useAdmin } from "@/hooks/use-admin";
import { PropertySEOSafe } from "@/components/seo/PropertySEOSafe";
import { getPropertyUrl } from "@/utils/slugify";
import { PropertyConfigDisplay } from "@/components/public/PropertyConfigDisplay";

interface Property {
  id: string;
  title: string;
  locality?: string | null;
  city: string | null;
  state: string | null;
  address: string | null;
  price: number | null;
  price_on_request?: boolean | null;
  price_min?: number | null;
  price_max?: number | null;
  property_type: string;
  area_sqft?: number | null;
  area_min?: number | null;
  area_max?: number | null;
  bedrooms?: number | null;
  bedrooms_options?: number[] | null;
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  amenities?: string[] | null;
  is_featured: boolean | null;
  created_at: string;
  updated_at?: string;
  latitude?: number | null;
  longitude?: number | null;
  rera_id?: string | null;
  possession_date?: string | null;
  project_name?: string | null;
  developer_name?: string | null;
  parking_spaces?: number | null;
  floor_number?: number | null;
  total_floors?: number | null;
  youtube_url?: string | null;
  slug?: string | null;
  furnishing?: string | null;
}

export default function PropertyDetail({ initialProperty }: { initialProperty?: Property | null } = {}) {
  const { isStaff } = useAdmin();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(initialProperty ?? null);
  const [loading, setLoading] = useState(!initialProperty);
  const [error, setError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const handleImagesUpdated = async (newImages: string[]) => {
    if (property && newImages.length > 0) {
      try {
        const { error: updateError } = await supabase
          .from('properties')
          .update({ images: newImages })
          .eq('id', property.id);
        if (!updateError) {
          setProperty(prev => prev ? { ...prev, images: newImages } : prev);
        }
      } catch (error) {
        console.error('Failed to update property images:', error);
      }
    }
  };

  useEffect(() => {
    if (id) {
      if (!property || (property.id !== id && property.slug !== id)) {
        fetchProperty(id);
      }
    }
  }, [id]);

  const fetchProperty = async (slugOrId: string) => {
    try {
      setLoading(true);
      setError(null);
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(slugOrId);
      const selectFields = `id, title, description, property_type, price, price_on_request, price_min, price_max, area_sqft, area_min, area_max, bedrooms, bedrooms_options, bathrooms, locality, address, city, state, pincode, rera_id, possession_date, images, amenities, is_featured, is_published, latitude, longitude, developer_name, parking_spaces, floor_number, total_floors, youtube_url, furnishing, created_at, updated_at, project_name, slug`;
      
      const resp = await (supabase as any)
        .from("properties")
        .select(selectFields)
        .eq("is_published", true)
        .eq(isUuid ? "id" : "slug", slugOrId)
        .maybeSingle();

      if (resp.error) throw resp.error;
      if (!resp.data || resp.data.deleted_at) throw new Error('Property not found or not published');
      setProperty(resp.data);
    } catch (err) {
      console.error("Error fetching property:", err);
      setError("Property not found or no longer available.");
    } finally {
      setLoading(false);
    }
  };

  const getPropertyTypeLabel = (type: string) => type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const getLocationString = (prop: Property) => [prop.locality, prop.city, prop.state].filter(Boolean).join(', ');

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-[60vh] w-full rounded-xl" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-64 w-full rounded-xl" />
              <Skeleton className="h-48 w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
          <Alert variant="destructive"><AlertDescription>{error || "Property not found"}</AlertDescription></Alert>
        </div>
      </div>
    );
  }

  // Build specs array for the icon grid
  const areaDisplay = (property.area_min && property.area_max && property.area_min !== property.area_max)
    ? `${property.area_min.toLocaleString()} - ${property.area_max.toLocaleString()}`
    : property.area_sqft
      ? `${property.area_sqft.toLocaleString()}`
      : null;

  const bedroomsDisplay = property.bedrooms_options?.length
    ? formatBedroomOptions(property.bedrooms_options)
    : property.bedrooms
      ? String(property.bedrooms)
      : null;

  const specs = [
    areaDisplay && { icon: Square, value: areaDisplay, label: "Sq Ft" },
    bedroomsDisplay && { icon: Bed, value: bedroomsDisplay, label: property.bedrooms_options?.length ? "" : "BHK" },
    property.bathrooms && { icon: Bath, value: property.bathrooms, label: "Bathrooms" },
    property.parking_spaces && { icon: Car, value: property.parking_spaces, label: "Parking" },
    property.floor_number != null && { icon: Layers, value: `${property.floor_number}${property.total_floors ? `/${property.total_floors}` : ''}`, label: "Floor" },
    (!property.floor_number && property.total_floors) && { icon: Building2, value: property.total_floors, label: "Floors" },
    property.furnishing && { icon: Sofa, value: property.furnishing.replace('-', ' '), label: "Furnishing" },
  ].filter(Boolean) as { icon: any; value: any; label: string }[];

  const detailItems = [
    property.address && { label: "Address", value: property.address },
    property.project_name && { label: "Project", value: property.project_name },
    property.developer_name && { label: "Developer", value: property.developer_name },
    property.possession_date && { label: "Possession", value: formatDate(property.possession_date) },
    property.rera_id && { label: "RERA", value: property.rera_id },
    { label: "Listed", value: formatDate(property.created_at) },
    property.updated_at && { label: "Updated", value: formatDate(property.updated_at) },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <>
      <PropertySEOSafe
        property={{ ...property, location: property.locality || property.city || '' }}
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://mnre.in'}${getPropertyUrl(property)}`}
      />
      <div className="min-h-screen bg-background">
        <Navbar />

        {/* Breadcrumb + actions bar */}
        <div className="border-b bg-background sticky top-16 z-30">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <FavoriteButton propertyId={property.id} />
              <PropertyShare property={{ ...property, location: property.locality }} />
              <WhatsAppShare
                property={{ ...property, location: property.locality }}
                phoneNumber="+917021011304"
                variant="outline"
                size="sm"
                showText={false}
              />
              {isStaff && (
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin?edit=${property.id}`)}>
                  <Edit className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                <EnhancedPropertyImageGallery
                  images={property.images || []}
                  propertyTitle={property.title}
                  propertyType={property.property_type}
                  reraNumber={property.rera_id || undefined}
                  property={{
                    name: property.title,
                    developer: property.developer_name || property.project_name || 'Developer',
                    propertyType: property.property_type,
                    bhkOptions: property.bedrooms ? [formatBedroomLabel(property.bedrooms)] : ['2 BHK'],
                    area: property.locality || property.city || '',
                    areaMin: property.area_sqft || 1000,
                    areaMax: property.area_sqft || 2000,
                    priceMin: property.price || 0,
                    priceMax: property.price || 0,
                    location: getLocationString(property),
                    description: property.description || `Premium ${property.property_type} in ${property.locality || property.city || 'prime location'}`,
                    amenities: property.amenities || [],
                    status: 'Under Construction',
                    possession: property.possession_date || new Date().toISOString().split('T')[0],
                    rera: property.rera_id || 'TBD'
                  }}
                  onImagesUpdated={handleImagesUpdated}
                />
              </motion.div>

              {/* Title + Price (mobile: below gallery, desktop: below gallery) */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="secondary">{getPropertyTypeLabel(property.property_type)}</Badge>
                      {property.is_featured && <Badge className="bg-primary">Featured</Badge>}
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                      {property.title}
                      {property.project_name && property.project_name !== property.title && (
                        <span className="text-muted-foreground font-normal text-lg md:text-xl"> — {property.project_name}</span>
                      )}
                    </h1>
                    {(property.bedrooms_options?.length || property.bedrooms || property.developer_name) && (
                      <p className="text-base text-muted-foreground mt-1">
                        {[
                          property.bedrooms_options?.length
                            ? formatBedroomOptions(property.bedrooms_options)
                            : property.bedrooms ? formatBedroomLabel(property.bedrooms) : null,
                          property.property_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                          property.developer_name ? `by ${property.developer_name}` : null,
                        ].filter(Boolean).join(' • ')}
                      </p>
                    )}
                    <div className="flex items-center text-muted-foreground mt-1.5 text-sm">
                      <MapPin className="h-4 w-4 mr-1 shrink-0" />
                      <span>{getLocationString(property)}</span>
                    </div>
                    <div className="mt-2">
                      <PropertyRatingCompact propertyId={property.id} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-3xl md:text-4xl font-bold text-primary">
                      {formatPriceDisplay({ price_on_request: property.price_on_request, price: property.price, price_min: property.price_min ?? null, price_max: property.price_max ?? null })}
                      <span className="text-lg align-super">*</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Specs grid */}
              {specs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.4 }}
                >
                  <div className={`grid gap-3 ${specs.length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3 md:grid-cols-4 lg:grid-cols-' + Math.min(specs.length, 6)}`}>
                    {specs.map((spec, i) => (
                      <div key={i} className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl border">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <spec.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground capitalize truncate">{spec.value}</div>
                          <div className="text-xs text-muted-foreground">{spec.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Pricing & Configurations */}
              <PropertyConfigDisplay propertyId={property.id} />

              {/* Description */}
              {property.description && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-3">About this Property</h3>
                    <div className="text-foreground/80 leading-relaxed whitespace-pre-line">
                      {property.description.length > 700 && !isDescriptionExpanded
                        ? `${property.description.substring(0, 700)}...`
                        : property.description}
                    </div>
                    {property.description.length > 700 && (
                      <Button variant="link" onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)} className="mt-2 p-0 h-auto text-primary">
                        {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* YouTube */}
              {property.youtube_url && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-3">Property Video</h3>
                    <YouTubeEmbed url={property.youtube_url} />
                  </CardContent>
                </Card>
              )}

              {/* Amenities */}
              {property.amenities && property.amenities.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {property.amenities.map((amenity, index) => (
                        <div key={index} className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                          <span className="text-sm">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Property Details */}
              {detailItems.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Property Details</h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      {detailItems.map((item, i) => (
                        <div key={i} className="flex justify-between py-2 border-b border-border/50 last:border-0">
                          <span className="text-muted-foreground">{item.label}</span>
                          <span className="font-medium text-right">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Documents */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Floor Plans & Brochures</h3>
                  <PropertyDocuments propertyId={property.id} />
                </CardContent>
              </Card>

              {/* Location Map */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Location</h3>
                  <PropertyMap
                    properties={[{
                      id: property.id, title: property.title,
                      location: getLocationString(property),
                       latitude: property.latitude ?? undefined, longitude: property.longitude ?? undefined,
                       price: property.price ?? 0, property_type: property.property_type
                    }]}
                    center={property.latitude && property.longitude ? [property.longitude, property.latitude] : undefined}
                    zoom={15} height="300px" showControls
                  />
                  {property.address && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium">{property.address}</p>
                        <p className="text-sm text-muted-foreground">{getLocationString(property)}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sticky Sidebar */}
            <div className="space-y-6">
              <div className="lg:sticky lg:top-32">
                {/* CTA Card */}
                <Card className="shadow-md mb-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-1">Interested in this property?</h3>
                    <p className="text-sm text-muted-foreground mb-5">Get in touch for a viewing or more details</p>

                    <AuthGatedDialog
                      trigger={
                        <Button className="w-full mb-3 h-11 text-base font-semibold">
                          <Phone className="h-4 w-4 mr-2" />
                          Send Inquiry
                        </Button>
                      }
                      title="Property Inquiry"
                    >
                      <InquiryForm propertyId={property.id} propertyTitle={property.title} />
                    </AuthGatedDialog>

                    <AuthGatedDialog
                      trigger={
                        <Button className="w-full">
                          <Calendar className="h-4 w-4 mr-2" />
                          Schedule Viewing
                        </Button>
                      }
                      title="Schedule Property Viewing"
                    >
                      <AppointmentBooking
                        propertyId={property.id}
                        propertyTitle={property.title}
                        propertyLocation={property.locality || property.city || ''}
                        isEmbedded
                      />
                    </AuthGatedDialog>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <WhatsAppShare
                        property={{ ...property, location: property.locality }}
                        phoneNumber="+917021011304"
                        variant="outline"
                        size="sm"
                      />
                      <EmailShare
                        property={{ ...property, location: property.locality }}
                        recipientEmail="hello@mnre.in"
                        recipientName="Property Team"
                        variant="outline"
                        size="sm"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Agent Card */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-base font-semibold mb-4">Contact Agent</h3>
                    <div className="space-y-3">
                      <a href="tel:+917021011304" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">+91 7021011304</div>
                          <div className="text-xs text-muted-foreground">Call anytime</div>
                        </div>
                      </a>
                      <a href="mailto:hello@mnre.in" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">hello@mnre.in</div>
                          <div className="text-xs text-muted-foreground">Email us</div>
                        </div>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Price & Images Disclaimer */}
          <div className="mt-8 px-1">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold">*</span> Images shown are for representation purposes only and may not reflect the actual property. Prices mentioned are subject to change without prior notice. Please contact us for the latest pricing and availability.
            </p>
          </div>

          {/* Full-width sections below — lazy loaded */}
          <div className="mt-10 space-y-8">
            <LazySection>
              <NeighborhoodInsights property={{ ...property, location: property.locality || property.city || '' }} />
            </LazySection>
            <LazySection>
              <MortgageCalculator propertyPrice={property.price || 0} />
            </LazySection>
            <LazySection>
              <PropertyRating propertyId={property.id} />
            </LazySection>
            <LazySection>
              <SimilarProperties property={{ ...property, location: property.locality || property.city || '' }} />
            </LazySection>
          </div>
        </div>
      </div>
    </>
  );
}
